import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, Phone, Send, Check, AlertCircle, Loader2, Facebook, Instagram, Linkedin } from 'lucide-react';
import { COMPANY } from '../mock';
import PartnerBanner from '../components/PartnerBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', telephone: '', sujet: 'Panneaux solaires', message: '', accept: false });
  const [status, setStatus] = useState({ state: 'idle', message: '' }); // idle | loading | success | error

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept) return;
    setStatus({ state: 'loading', message: '' });
    try {
      const res = await axios.post(`${API}/contact`, form, { timeout: 25000 });
      setStatus({ state: 'success', message: res.data.message || 'Votre demande a bien été envoyée.' });
      setForm({ nom: '', prenom: '', email: '', telephone: '', sujet: 'Panneaux solaires', message: '', accept: false });
      setTimeout(() => setStatus({ state: 'idle', message: '' }), 8000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer ou de nous contacter par email.";
      setStatus({ state: 'error', message: msg });
    }
  };

  return (
    <div>
      <section className="pt-36 pb-12 bg-grad-hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="eyebrow">Contact</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] text-gray-900 max-w-4xl">Parlons de votre <span className="italic-accent">projet énergétique.</span></h1>
          <p className="mt-6 text-gray-600 max-w-2xl">Nos experts répondent à vos questions et se déplacent chez vous en Suisse romande pour une étude personnalisée et gratuite.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl text-gray-900 mb-2">Nos coordonnées</h2>
            <p className="text-gray-600 mb-8">Nous restons à votre disposition pour toute question.</p>
            <ul className="space-y-6">
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full bg-[#80b23f]/15 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#80b23f]" /></span><div><div className="text-sm uppercase tracking-widest text-gray-500">Adresse</div><div className="font-medium text-gray-900">{COMPANY.address}</div></div></li>
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full bg-[#80b23f]/15 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#80b23f]" /></span><div><div className="text-sm uppercase tracking-widest text-gray-500">Email</div><a href={`mailto:${COMPANY.email}`} className="font-medium text-gray-900 hover:text-[#125082] break-all">{COMPANY.email}</a></div></li>
              <li className="flex gap-4"><span className="w-11 h-11 rounded-full bg-[#80b23f]/15 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#80b23f]" /></span><div><div className="text-sm uppercase tracking-widest text-gray-500">Téléphone</div><a href="tel:+41225187155" className="font-medium text-gray-900 hover:text-[#125082]">022 518 71 55</a></div></li>
            </ul>
            <div className="flex gap-3 mt-10">
              <a href={COMPANY.socials.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#125082] text-white hover:bg-[#80b23f] flex items-center justify-center transition-colors"><Facebook className="w-5 h-5" /></a>
              <a href={COMPANY.socials.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#125082] text-white hover:bg-[#80b23f] flex items-center justify-center transition-colors"><Instagram className="w-5 h-5" /></a>
              <a href={COMPANY.socials.linkedin} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#125082] text-white hover:bg-[#80b23f] flex items-center justify-center transition-colors"><Linkedin className="w-5 h-5" /></a>
            </div>
            <div className="mt-10 rounded-2xl overflow-hidden border border-gray-200">
              <iframe title="Carte Hermance" src="https://www.openstreetmap.org/export/embed.html?bbox=6.2395%2C46.3017%2C6.2497%2C46.3037&layer=mapnik&marker=46.30271597132351%2C6.244580854090585" className="w-full h-64" loading="lazy" />
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-[#f1f1f1] p-8 lg:p-10 rounded-3xl">
            <h2 className="font-display text-3xl text-gray-900 mb-2">Demander un devis</h2>
            <p className="text-gray-600 mb-7">Remplissez le formulaire ci-dessous. Nous vous recontactons dans les 24h.</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Prénom *" value={form.prenom} onChange={handle('prenom')} required />
              <Field label="Nom *" value={form.nom} onChange={handle('nom')} required />
              <Field label="Email *" type="email" value={form.email} onChange={handle('email')} required />
              <Field label="Téléphone" value={form.telephone} onChange={handle('telephone')} />
            </div>
            <div className="mt-4">
              <label className="block text-xs uppercase tracking-widest text-gray-600 font-semibold mb-2">Sujet</label>
              <select value={form.sujet} onChange={handle('sujet')} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#125082]/30">
                <option>Panneaux solaires</option>
                <option>Pompes à chaleur</option>
                <option>Batteries de stockage</option>
                <option>Boilers thermodynamiques</option>
                <option>Etude énergétique complète</option>
                <option>Autre demande</option>
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-xs uppercase tracking-widest text-gray-600 font-semibold mb-2">Message *</label>
              <textarea required value={form.message} onChange={handle('message')} rows={5} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#125082]/30" placeholder="Décrivez votre projet (type de logement, surface, système actuel...)" />
            </div>
            <label className="mt-5 flex items-start gap-3 text-sm text-gray-600">
              <input type="checkbox" checked={form.accept} onChange={handle('accept')} className="mt-1 accent-[#125082]" required />
              <span>J'accepte que mes données soient utilisées pour me recontacter, conformément à la <a href={COMPANY.legal.politique} target="_blank" rel="noreferrer" className="text-[#125082] underline">politique de confidentialité</a>.</span>
            </label>
            <button type="submit" disabled={!form.accept || status.state === 'loading'} className="btn-primary mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {status.state === 'loading' ? (<><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours...</>) : (<>Envoyer la demande <Send className="w-4 h-4" /></>)}
            </button>
            {status.state === 'success' && (
              <div className="mt-5 p-4 bg-[#80b23f]/15 border border-[#80b23f]/40 rounded-xl text-sm text-[#125082] flex items-center gap-2">
                <Check className="w-4 h-4 text-[#80b23f] shrink-0" /> {status.message}
              </div>
            )}
            {status.state === 'error' && (
              <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {status.message}
              </div>
            )}
          </form>
        </div>
      </section>
      <PartnerBanner />
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gray-600 font-semibold mb-2">{label}</label>
      <input {...props} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#125082]/30" />
    </div>
  );
}
